import { useTranslation, Trans } from "react-i18next";
import { FiAlertCircle, FiCheckCircle, FiZap, FiStar, FiAward } from "react-icons/fi";
import styles from "./ConfirmUpdateModal.module.css";
import { formatDate } from "../../../utils/formatDateTime";
import { predictYearlyTerm, predictUpgradeCredit } from "../../../utils/yearlyTerm";

const ConfirmUpdateModal = ({ isOpen, plan, subscription, currentPlanPrice, onConfirm, onCancel, loading }) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  // For a yearly plan, show the exact term this payment buys BEFORE the redirect: the term
  // starts today (an active monthly is cut at the switch) or extends an already-running term —
  // the customer should never have to infer which.
  const term = plan?.billingType === "YEARLY" ? predictYearlyTerm(subscription, plan.code) : null;
  // Upgrading from an active monthly credits its unused days against the yearly price. The
  // backend computes the authoritative charge at initiate; this mirror shows the same numbers
  // up front so the redirect amount is never a surprise.
  const upgradeCredit = plan?.billingType === "YEARLY" && !term?.extension
    ? predictUpgradeCredit(subscription, currentPlanPrice)
    : 0;
  const netAmount = upgradeCredit > 0 ? Math.round((Number(plan.price) - upgradeCredit) * 100) / 100 : null;

  const getPlanIcon = (code) => {
    const iconMap = {
      STANDARD: <FiStar />,
      REGULAR: <FiAward />,
      ENTERPRISE: <FiZap />,
    };
    return iconMap[code] || <FiStar />;
  };

  const getPlanIconGradient = (code) => {
    const gradientMap = {
      STANDARD: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
      REGULAR: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      ENTERPRISE: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    };
    return gradientMap[code] || "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)";
  };

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div
            className={styles.modalIcon}
            style={{ background: plan ? getPlanIconGradient(plan.code) : "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)" }}
          >
            {plan ? getPlanIcon(plan.code) : <FiAlertCircle />}
          </div>
          <h3 className={styles.title}>{t("pages.confirmUpdate.title")}</h3>
        </div>

        <p className={styles.message}>
          <Trans i18nKey="pages.confirmUpdate.message" values={{ plan: plan?.name }} components={{ strong: <strong /> }} />
        </p>

        {plan && (
          <div className={styles.planInfo}>
            <div className={styles.planInfoHeader}>
              <FiCheckCircle className={styles.planInfoIcon} />
              <span className={styles.planInfoTitle}>{t("pages.confirmUpdate.planInfo")}</span>
            </div>
            <div className={styles.planInfoContent}>
              <div className={styles.planInfoRow}>
                <span className={styles.planInfoLabel}>{t("pages.confirmUpdate.planName")}</span>
                <span className={styles.planInfoValue}>{plan.name}</span>
              </div>
              <div className={styles.planInfoRow}>
                <span className={styles.planInfoLabel}>
                  {plan.billingType === "MONTHLY"
                    ? t("pages.confirmUpdate.monthlyPrice")
                    : t("pages.confirmUpdate.price", { defaultValue: "Qiymət" })}
                </span>
                <span className={styles.planInfoValue}>
                  <span className={styles.priceAmount}>{plan.price}</span>
                  <span className={styles.priceCurrency}>{t("auth.subscriptionPlans.currency")}</span>
                  <span className={styles.pricePeriod}>
                    {plan.billingType === "ONE_TIME"
                      ? t("pages.subscription.perOneTime", { defaultValue: "" })
                      : plan.billingType === "YEARLY"
                        ? t("pages.subscription.perYear", { defaultValue: "/il" })
                        : t("auth.subscriptionPlans.perMonth")}
                  </span>
                </span>
              </div>
              {term && (
                <div className={styles.planInfoRow}>
                  <span className={styles.planInfoLabel}>
                    {term.extension
                      ? t("pages.confirmUpdate.licenseNewEnd", { defaultValue: "Yeni bitmə tarixi" })
                      : t("pages.confirmUpdate.licenseTerm", { defaultValue: "Abunəlik müddəti" })}
                  </span>
                  <span className={styles.planInfoValue}>
                    {term.extension
                      ? formatDate(term.end)
                      : `${formatDate(term.start)} – ${formatDate(term.end)}`}
                  </span>
                </div>
              )}
              {upgradeCredit > 0 && (
                <>
                  <div className={styles.planInfoRow}>
                    <span className={styles.planInfoLabel}>
                      {t("pages.confirmUpdate.upgradeCredit", { defaultValue: "Aylıq abunəlikdən kredit" })}
                    </span>
                    <span className={styles.planInfoValue} style={{ color: "#047857", fontWeight: 700 }}>
                      −{upgradeCredit.toFixed(2)} {t("auth.subscriptionPlans.currency")}
                    </span>
                  </div>
                  <div className={styles.planInfoRow}>
                    <span className={styles.planInfoLabel}>
                      {t("pages.confirmUpdate.totalToPay", { defaultValue: "Ödəniləcək məbləğ" })}
                    </span>
                    <span className={styles.planInfoValue} style={{ fontWeight: 700 }}>
                      {netAmount.toFixed(2)} {t("auth.subscriptionPlans.currency")}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12.5, color: "#64748b", lineHeight: 1.45 }}>
                    {t("pages.confirmUpdate.monthlyEndsNow", {
                      defaultValue: "Aylıq abunəliyiniz keçid anında bağlanır — istifadə olunmamış günlərin dəyəri qiymətdən çıxılıb.",
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button
            className={styles.cancelBtn}
            onClick={onCancel}
            disabled={loading}
          >
            {t("common.cancel")}
          </button>
          <button
            className={styles.confirmBtn}
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? t("pages.confirmUpdate.updating") : t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmUpdateModal;
